
import React, { startTransition } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSidebar } from '@/components/ui/sidebar';

interface NavItemProps {
  icon: LucideIcon;
  label: string;
  path?: string;
  external?: boolean;
  isActive?: boolean;
  onClick?: () => void;
  className?: string;
}

const NavItem: React.FC<NavItemProps> = ({
  icon: Icon,
  label,
  path,
  external = false,
  isActive: propIsActive,
  onClick,
  className = ''
}) => {
  const location = useLocation();
  const { state } = useSidebar();
  const isActive = propIsActive ?? (path && location.pathname === path);
  const isCollapsed = state === 'collapsed';

  console.log(`NavItem clicked: ${label}, external: ${external}, path: ${path}`);

  const handleClick = () => {
    if (onClick) {
      startTransition(() => {
        onClick();
      });
    }
  };

  const baseClasses = cn(
    'flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm font-medium group',
    'hover:bg-gray-50 hover:text-gray-900',
    isActive
      ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600'
      : 'text-gray-600',
    isCollapsed && 'justify-center px-2',
    className
  );

  const iconClasses = cn(
    'h-4 w-4',
    isCollapsed && 'h-5 w-5'
  );

  if (external && path) {
    return (
      <a
        href={path}
        target="_blank"
        rel="noopener noreferrer"
        className={baseClasses}
        onClick={handleClick}
        title={isCollapsed ? label : undefined}
      >
        <Icon className={iconClasses} />
        {!isCollapsed && <span>{label}</span>}
      </a>
    );
  }

  if (path) {
    return (
      <Link 
        to={path} 
        className={baseClasses} 
        onClick={handleClick}
        title={isCollapsed ? label : undefined}
      >
        <Icon className={iconClasses} />
        {!isCollapsed && <span>{label}</span>}
      </Link>
    );
  }

  return (
    <button 
      className={baseClasses} 
      onClick={handleClick}
      title={isCollapsed ? label : undefined}
    >
      <Icon className={iconClasses} />
      {!isCollapsed && <span>{label}</span>}
    </button>
  );
};

export default NavItem;
